import { createPortal } from "react-dom";

/**
 * Shown when the coach tries to sign out ONLINE but the pre-logout upload of
 * pending plays/games failed (expired session, server error). Signing out
 * would wipe that data, so block the wipe and let them cancel (re-login /
 * retry first) or explicitly log out anyway.
 *
 * Portaled to document.body: the app sidebars render this from inside their
 * drawer (fixed z-[99990]), which is its own stacking context — rendered
 * inline, the dialog's z-[130000] would be trapped inside the drawer's layer
 * and paint UNDER the session-expired modal (z-[125000], root level).
 * pointer-events:auto re-enables interaction if a Radix modal has locked the
 * body with pointer-events:none.
 */
export function LogoutSyncFailedWarning({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[130000] flex items-center justify-center bg-black/25 px-6 backdrop-blur-[1.25px]"
      style={{ pointerEvents: "auto" }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">
          Your data couldn&rsquo;t be uploaded
        </h2>
        <p className="mb-5 text-sm text-gray-600">
          Some plays or games on this device haven&rsquo;t been saved to the
          server yet, and uploading them just failed — your session may have
          expired. Signing out now will permanently delete them. Cancel and
          sign in again when prompted so they can be saved first.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            Log out anyway
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LogoutSyncFailedWarning;
